import { useContext, useState } from 'react';
import Cross from 'assets/svg/Cross';
import { Select, Spin, Switch, Tooltip } from 'antd';
import styled from '@emotion/styled';
import _ from 'lodash';
import { useScrollSync } from 'hooks/custom/useScrollSync';
import { TPODataContext } from 'contexts/TPOContext';
import { ProductPPG, PromoTactic } from 'types/tradePromoOptimization';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig, getEditButtonState, sendNotification } from 'utils/helperMethods';
import KPISelector from './KPISelector';
import { MetricsApiTriggerPoints } from '../Promos';
import { colorConstants } from 'config/colorConstants';
import { ReloadOutlined } from '@ant-design/icons';
import { antIcon } from 'components/pages/Login';
import { PromotionTypes, tpoPromoMetricKeys } from 'utils/Enums';
import { checkPromoStatus, getPromoMetricByNameKeyName, hasInvalidMetricsForModelUplift } from '../../tpoHelperMethods';
import { TpoMessages } from 'constants/tpo';
import MetricsSection from './MetricsSection';

export const PromoSelect = styled(Select)`
    .ant-select-arrow-loading {
        color: blue !important;
        font-size: 16px;
    }
    .ant-select-selector {
        border-radius: 6px !important;
        border: none !important;
    }
    &.ant-select-multiple .ant-select-selection-item-remove {
        position: relative;
        top: -2px;
    }
`;

interface IProps {
    setFetchMetricsTriggeredFrom: React.Dispatch<React.SetStateAction<MetricsApiTriggerPoints>>;
    mechanicType: string;
}

const promoSyncId = 'promo_details_sync_scroll';

const PromoMetricDetails: React.FC<IProps> = ({ setFetchMetricsTriggeredFrom, mechanicType }) => {
    const {
        dimensionConfigData,
        selectedEventInfo,
        updateSelectedEventProperty,
        tempEventProperties,
        selectedPromoPPGIds,
        setSelectedPromoPPGIds,
        viewMode,
        isOptimizationInProgress,
        isCurrentPromoFormDisabled,
        isTpmTab,
        isValid,
        promoConfigDictionary,
        upliftModelMutation,
        finalCalenderStatues,
    } = useContext(TPODataContext);

    const { appConfig } = useContext(UserContext);
    const { promoLimit } = getConfig('tradePromoOptimization', appConfig);
    const [isPopoverVisible, setIsPopoverVisible] = useState(false);

    const currentPromoTactic = selectedEventInfo?.currentPromoTactic;
    const [showMetrics, setShowMetrics] = useState(true);
    const [showTotals, setShowTotals] = useState(false);
    const [showSpendDetails, setShowSpendDetails] = useState(true);

    const isPostAuditOrWriteOff = [PromotionTypes.Post_Audit, PromotionTypes.Write_Off].includes(
        Number(selectedEventInfo?.currentPromoTactic?.promoType),
    );
    const combinedViewMode = isOptimizationInProgress || viewMode || isTpmTab || isCurrentPromoFormDisabled;
    const promoStatus = selectedEventInfo?.currentPromoTactic?.status?.toLowerCase();
    const planStatus = finalCalenderStatues?.approval_status?.toLowerCase();
    const editButtonDisabledState = getEditButtonState(planStatus, selectedEventInfo)?.isDisabled;
    const ppgAddDelDisabled =
        checkPromoStatus(promoStatus as string) ||
        !!editButtonDisabledState ||
        selectedEventInfo?.currentPromoTactic?.offerType === 'promo_library' ||
        isPostAuditOrWriteOff;

    const refreshUplifBtnDisabled = !selectedPromoPPGIds.length || !isValid;

    // Use the custom hook with the class names of the scrollable elements
    useScrollSync({ elementSelectors: [promoSyncId], syncHorizontal: true });

    const onPromoAddClick = () => {
        if ((selectedEventInfo?.promoTacticsIdNamePair?.length || 0) > promoLimit) {
            sendNotification('error', `Upto ${promoLimit} promos can be added`);
        }
    };

    const onProductUnselect = async (id: number) => {
        if (combinedViewMode) return;
        if (selectedEventInfo?.currentPromoTactic) {
            const updatedPPGs = selectedEventInfo.currentPromoTactic.ppgs.filter((ppg: ProductPPG) => ppg.id !== id);
            const updatedTactic = {
                ...selectedEventInfo?.currentPromoTactic,
                ppgs: updatedPPGs,
                hasChanges: true,
            };
            updateSelectedEventProperty('currentPromoTactic', updatedTactic);
            const newList = selectedPromoPPGIds.filter((promoId: number) => promoId !== id);

            setSelectedPromoPPGIds(selectedPromoPPGIds.filter((promoId: number) => promoId !== id));
        }
    };
    const onProductSelectHandler = async (updatedList: string[]) => {
        const newPPgs: Number[] = [];
        let removedPPgs: Number[] = [];

        setSelectedPromoPPGIds((prev: number[]) => {
            const currentSet = new Set(prev);
            removedPPgs = prev.filter((item: number) => !updatedList.includes(item?.toString()));

            return updatedList.map((item: string) => {
                const ppgId = Number(item);
                if (!currentSet.has(ppgId)) {
                    newPPgs.push(ppgId);
                }
                return ppgId;
            });
        });
        if (selectedEventInfo?.currentPromoTactic) {
            //  if ppgs are only deleted, no need for api call just filter the current ppgs here
            // else the api success will take care of the filtering and adding
            const filteredPPgs =
                !newPPgs.length && removedPPgs.length
                    ? selectedEventInfo?.currentPromoTactic?.ppgs?.filter((item) => !removedPPgs.includes(item.id))
                    : selectedEventInfo?.currentPromoTactic?.ppgs;

            const updatedTactic = {
                ...selectedEventInfo?.currentPromoTactic,
                newAddedPPgs: newPPgs,
                deletedPPgs: removedPPgs,
                ppgs: filteredPPgs,
                hasChanges: true,
            };
            updateSelectedEventProperty('currentPromoTactic', updatedTactic);
        }
        setFetchMetricsTriggeredFrom(MetricsApiTriggerPoints.PPG);
    };

    const getProductName = (name: string) => {
        return (
            <Tooltip
                placement="top"
                color="white"
                destroyTooltipOnHide
                overlayStyle={{ borderRadius: 4 }}
                overlayInnerStyle={{
                    color: colorConstants.darkGrey,
                }}
                title={name}
            >
                <span className="block w-[150px] truncate">{name}</span>
            </Tooltip>
        );
    };

    const handleRefreshRecommClick = async () => {
        if (
            hasInvalidMetricsForModelUplift(promoConfigDictionary, selectedEventInfo?.currentPromoTactic as PromoTactic)
        ) {
            return;
        }
        await upliftModelMutation?.mutateAsync({ mechanicType, ppgs: currentPromoTactic?.ppgs });
    };

    const isNoPPGSelected = currentPromoTactic?.ppgs?.length === 0;

    const ppgs = selectedEventInfo?.currentPromoTactic?.ppgs;
    const hasPPG = !isNoPPGSelected && Array.isArray(ppgs) && ppgs.length > 0;

    const metricsExists = hasPPG && !!ppgs[0]?.metrics?.length;
    const spendExists = hasPPG && !!ppgs[0]?.spendDetails?.length;

    return (
        <div className="bg-backgroundGreyAlt6 rounded pb-3 pt-2 flex flex-1 flex-col overflow-hidden ">
            {!isValid ? (
                <div className=" flex-1  flex justify-center items-center">
                    {`Please select all the required fields`}
                </div>
            ) : selectedEventInfo?.currentPromoTactic?.isDataLoading ? (
                <div className="bg-backgroundGreyAlt6 rounded w-full min-h-0 flex-1 pr-5 pl-6  flex justify-center items-center">
                    <Spin />
                </div>
            ) : (
                <div className=" bg-backgroundGreyAlt6 rounded flex flex-1 flex-col overflow-hidden pr-5 pl-3">
                    <div className="flex justify-between items-center">
                        <span className="font-bold">{`${combinedViewMode ? 'View' : 'Add'} Metric Details`}</span>

                        <div className="flex justify-center items-center gap-2">
                            <div className="">
                                <span className="mr-2 font-xs text-blackAlt">Total</span>
                                <Switch checked={showTotals} onChange={(val) => setShowTotals(val)} />{' '}
                            </div>
                            {!combinedViewMode && (
                                <div
                                    className="flex justify-center items-center gap-2 text-backgroundGreyAlt2 cursor-pointer"
                                    onClick={onPromoAddClick}
                                >
                                    {[PromotionTypes.Merchandising, PromotionTypes.SM_MDF].includes(
                                        Number(currentPromoTactic?.promoType),
                                    ) && (
                                        <button
                                            onClick={() => handleRefreshRecommClick()}
                                            className="flex justify-between items-center gap-2 rounded px-2 py-1 disabled:cursor-not-allowed"
                                            disabled={refreshUplifBtnDisabled}
                                        >
                                            {upliftModelMutation?.isPending ? (
                                                <Spin
                                                    indicator={antIcon}
                                                    style={{ color: '#000000a6', transform: 'scale(0.7)' }}
                                                />
                                            ) : (
                                                <ReloadOutlined />
                                            )}
                                            <span>Refresh Uplift % </span>
                                        </button>
                                    )}
                                    <KPISelector
                                        isVisible={isPopoverVisible}
                                        setIsVisible={setIsPopoverVisible}
                                        kpiList={
                                            tempEventProperties?.selectedDim1NestedAttributes?.map((p: any) => {
                                                return {
                                                    value: p.id?.toString(),
                                                    label: p.value,
                                                    isSelected: selectedPromoPPGIds?.includes(p.id),
                                                };
                                            }) || []
                                        }
                                        onSubmit={onProductSelectHandler}
                                        buttonName={`Select ${dimensionConfigData?.eventDim1NestedAttribute?.label}`}
                                        searchPlaceholder={`Search ${dimensionConfigData?.eventDim1NestedAttribute?.label}...`}
                                        variableName={dimensionConfigData?.eventDim1NestedAttribute?.label || ''}
                                        showSelectAll
                                        addDisabled={ppgAddDelDisabled}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {isNoPPGSelected ? (
                        <div className="h-full flex justify-center items-center">
                            {`Please select ${dimensionConfigData?.eventDim1NestedAttribute?.label} to add metrics`}
                        </div>
                    ) : (
                        <>
                            {/* PRODUCT NAME TILES  */}
                            <div
                                className={`ml-[260px] flex justify-start items-center overflow-x-scroll my-2 ${promoSyncId}`}
                            >
                                {showTotals && (
                                    <div className="flex  items-center py-1 px-4  min-w-[200px] max-w-[200px] bg-white border border-backgroundGreyAlt1 rounded-l-lg">
                                        Total
                                    </div>
                                )}

                                {currentPromoTactic?.ppgs
                                    ?.filter((product: ProductPPG) => product.isSelected)
                                    ?.map((product: ProductPPG, index: number, filteredArray) => {
                                        const isFirstTile = index === 0 && !showTotals;
                                        const isLastTile = index === filteredArray.length - 1;

                                        return (
                                            <div
                                                key={product.name}
                                                className={`flex justify-between items-center py-1 px-4 min-w-[200px] bg-white border border-backgroundGreyAlt1 ${
                                                    isFirstTile ? 'rounded-l-lg' : ''
                                                } ${isLastTile ? 'rounded-r-lg' : ''}`}
                                            >
                                                {getProductName(product.name)}
                                                <span
                                                    className={`pl-2 ${
                                                        ppgAddDelDisabled || viewMode
                                                            ? 'text-gray-400 cursor-not-allowed'
                                                            : 'cursor-pointer'
                                                    }`}
                                                    onClick={
                                                        ppgAddDelDisabled
                                                            ? () => {}
                                                            : () => onProductUnselect(product.id)
                                                    }
                                                >
                                                    <Cross />
                                                </span>
                                            </div>
                                        );
                                    })}
                            </div>

                            <MetricsSection
                                metricsExists={metricsExists}
                                spendExists={spendExists}
                                showMetrics={showMetrics}
                                setShowMetrics={setShowMetrics}
                                showSpendDetails={showSpendDetails}
                                setShowSpendDetails={setShowSpendDetails}
                                showTotals={showTotals}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default PromoMetricDetails;
